import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/clerk-react";
import { api } from "@/lib/api";
import type {
  GenerateRequest,
  GenerateResponse,
  GenerationStatusResponse,
} from "@/types";

// Start a new generation
export function useGenerate() {
  const { user } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: Omit<GenerateRequest, "user_id">) => {
      if (!user?.id) throw new Error("No user ID");

      return api.post<GenerateResponse>("/generate", {
        ...params,
        user_id: user.id,
      });
    },
    onSuccess: () => {
      // Refresh user data to get updated credit balance
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

// Poll a single generation status
// IMPORTANT: This endpoint can take 2+ minutes for ffmpeg-api operations (stretching, watermarking)
// The connection must stay open for the full duration
export function useGenerationStatus(generationId: string | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["generation", generationId],
    queryFn: async () => {
      if (!generationId) throw new Error("No generation ID");
      // Note: This request can take up to 3 minutes if ffmpeg-api processing is happening
      // Browser fetch has no default timeout, so connection stays open
      return api.get<GenerationStatusResponse>(
        `/generation/${generationId}/status`,
      );
    },
    enabled: !!generationId,
    refetchInterval: (query) => {
      const status = query.state.data?.generation?.status;
      // Stop polling when completed or failed
      if (status === "completed" || status === "failed") {
        // Refresh assets when done
        queryClient.invalidateQueries({ queryKey: ["assets"] });
        return false;
      }
      return 3000; // Poll every 3 seconds
    },
    // Continue polling even when tab is not focused
    refetchIntervalInBackground: true,
    // No retry on long requests - if it fails, let it fail
    retry: false,
  });
}
